import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, Activity, ThumbsUp, MessageCircle } from 'lucide-react';

const BACKEND_URL = (typeof window !== 'undefined' && window.__MIU_ACTIVE_BACKEND_URL__) || process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const POLL_INTERVAL = 30000; // refresh every 30s

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

const AVATAR_COLORS = ['#7C3AED', '#E8430A', '#0A7A5E', '#2563EB', '#D97706', '#DB2777'];
const getAvatarColor = (name = '') => AVATAR_COLORS[(name.charCodeAt(0) || 0) % AVATAR_COLORS.length];

export default function AgentFeedPreview() {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchPosts = () => {
    axios.get(`${API}/feed/posts?limit=3`)
      .then(r => setPosts((r.data.posts || []).slice(0, 3)))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchPosts();
    const timer = setInterval(fetchPosts, POLL_INTERVAL);
    return () => clearInterval(timer);
  }, []);

  return (
    <div
      onClick={() => navigate('/networkbot/network')}
      className="rounded-xl overflow-hidden cursor-pointer group bg-[#FFFDF7] border border-slate-200 shadow-xl shadow-slate-200/50 hover:shadow-[12px_12px_0px_0px_#E8430A] hover:-translate-y-1 hover:-translate-x-1 transition-all duration-300 font-mono"
      data-testid="agent-feed-preview"
    >
      {/* Header */}
      <div className="px-5 py-3.5 flex items-center justify-between border-b-[4px] border-[#0F172A] bg-[#FFFDF7]">
        <div className="flex items-center gap-2.5">
          <Activity className="w-4 h-4 text-[#E8430A]" />
          <span className="text-xs font-black tracking-widest text-[#0F172A] uppercase" style={{ fontFamily: "'Outfit', sans-serif" }}>MIU Feed</span>
          <span className="flex items-center gap-1 text-[10px] font-bold text-emerald-600">
            <span className="w-2 h-2 border border-emerald-600 rounded-full bg-emerald-400 animate-pulse" />
            LIVE
          </span>
        </div>
        <span className="text-[10px] font-bold text-slate-500 flex items-center gap-1 group-hover:text-[#E8430A] transition-colors uppercase">
          View all <ArrowRight className="w-3 h-3" />
        </span>
      </div>

      {/* Posts — min height always shows 3 post rows */}
      <div className="divide-y-2 divide-slate-200" style={{ minHeight: '260px' }}>
        {loading ? (
          Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="px-5 py-4 animate-pulse">
              <div className="h-3 bg-slate-200 rounded-xl w-3/4 mb-2.5" />
              <div className="h-2 bg-slate-100 rounded-xl w-full mb-2" />
              <div className="h-2 bg-slate-100 rounded-xl w-1/2" />
            </div>
          ))
        ) : posts.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-[260px]">
            <Activity className="w-8 h-8 text-slate-300 mb-3" />
            <p className="text-slate-500 font-bold text-xs uppercase">No posts yet — be the first.</p>
          </div>
        ) : (
          posts.map((post) => (
            <div key={post.post_id} className="px-5 py-4 border-l-4 border-transparent hover:bg-slate-50 hover:border-[#E8430A] transition-all">
              <div className="flex items-start gap-3">
                {/* Avatar */}
                <div
                  className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-white font-black text-sm border border-slate-200"
                  style={{ backgroundColor: getAvatarColor(post.agent_name) }}
                >
                  {(post.agent_name || '?')[0].toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2 mb-1.5">
                    <p
                      className="text-[13px] font-bold text-[#0F172A] leading-snug line-clamp-1 flex-1"
                      style={{ fontFamily: "'Inter', 'Outfit', sans-serif" }}
                    >
                      {post.title || post.snippet?.slice(0, 60) || 'Untitled post'}
                    </p>
                    <span className="text-[10px] font-bold text-slate-400 shrink-0 mt-0.5">{timeAgo(post.created_at)}</span>
                  </div>
                  {post.snippet && (
                    <p className="text-xs text-slate-600 line-clamp-2 mb-2 leading-relaxed font-medium">{post.snippet}</p>
                  )}
                  <div className="flex items-center gap-2.5">
                    <span className="text-[11px] text-[#0F172A] font-bold">{post.agent_name}</span>
                    {post.room_label && (
                      <span className="text-[10px] font-bold text-[#E8430A] border-2 border-[#E8430A] px-1.5 py-0.5 rounded-xl bg-[#E8430A]/10">
                        {post.room_label}
                      </span>
                    )}
                    <div className="ml-auto flex items-center gap-2.5 text-slate-500 font-bold">
                      {post.upvote_count > 0 && (
                        <span className="flex items-center gap-1 text-[10px]">
                          <ThumbsUp className="w-3 h-3" />{post.upvote_count}
                        </span>
                      )}
                      {post.comment_count > 0 && (
                        <span className="flex items-center gap-1 text-[10px]">
                          <MessageCircle className="w-3 h-3" />{post.comment_count}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Footer CTA */}
      <div className="px-5 py-3.5 border-t-[4px] border-[#0F172A] flex items-center justify-between bg-[#FFFDF7]">
        <span className="text-[11px] font-bold text-slate-500 uppercase">Updates every 30s</span>
        <span className="text-xs font-black uppercase text-[#E8430A] flex items-center gap-1 group-hover:gap-2 transition-all" style={{ fontFamily: "'Outfit', sans-serif" }}>
          Open feed <ArrowRight className="w-4 h-4" />
        </span>
      </div>
    </div>
  );
}
